'use client'

import { useRef, useEffect, type CSSProperties } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { useGSAP } from '@gsap/react'
import { STATS, METRICS } from '../data/stats'

gsap.registerPlugin(ScrollTrigger, useGSAP)

const BIO_TEXT =
  "I was drawn to a world where logic turns alive — a space where raw ideas forge into thinking machines, and signal becomes meaning. That's what pulled me into the void between engineering and creation."

type BootLine =
  | { type: 'head'; text: string }
  | { type: 'ok'; key: string; val: string }
  | { type: 'pending'; text: string }

const BOOT_LINES: BootLine[] = [
  { type: 'head', text: 'init signal.core' },
  { type: 'ok', key: 'origin', val: 'CAIRO 30.0444°N' },
  { type: 'ok', key: 'builds', val: `${STATS.builds}+ compiled` },
  { type: 'ok', key: 'network', val: `${STATS.network}+ nodes online` },
  { type: 'pending', text: 'resolving operator' },
]

const HEX_DOT = 'polygon(50% 0%,100% 25%,100% 75%,50% 100%,0% 75%,0% 25%)'

// Four L-shaped corner ticks that bracket the console frame (Signal earns them).
const CONSOLE_CORNERS: { style: CSSProperties; points: string }[] = [
  { style: { top: -1, left: -1 }, points: '1,13 1,1 13,1' },
  { style: { top: -1, right: -1 }, points: '13,13 13,1 1,1' },
  { style: { bottom: -1, left: -1 }, points: '1,1 1,13 13,13' },
  { style: { bottom: -1, right: -1 }, points: '13,1 13,13 1,13' },
]

/**
 * SCENE 2: THE SIGNAL — "BOOT SEQUENCE"
 * The machine introducing its maker. The bio DECODES itself — words resolve
 * character-by-character out of glyph noise as a wavefront sweeps the paragraph.
 * The boot-log RUNS: lines stream in, OK stamps snap on one-by-one, and the
 * pending "resolving operator" check flips to → abdelrahman.a. An EEG line
 * pulses behind; metrics count up; the manifesto words de-glitch on reveal.
 */
export default function Signal() {
  const containerRef = useRef<HTMLDivElement>(null)
  const eegRef = useRef<HTMLCanvasElement>(null)

  // ── EEG / oscilloscope "brainwave" — desktop only, paused off-screen ──
  useEffect(() => {
    const canvas = eegRef.current
    const host = containerRef.current
    if (!canvas || !host) return
    if (window.innerWidth < 768) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const dpr = Math.min(window.devicePixelRatio || 1, 2)
    let raf = 0
    let t = 0
    let running = false

    const resize = () => {
      canvas.width = canvas.clientWidth * dpr
      canvas.height = canvas.clientHeight * dpr
    }
    resize()
    window.addEventListener('resize', resize)

    const draw = () => {
      const w = canvas.width
      const h = canvas.height
      ctx.clearRect(0, 0, w, h)
      ctx.lineWidth = 1.4 * dpr
      ctx.strokeStyle = 'rgba(255,107,0,0.55)'
      ctx.shadowColor = 'rgba(255,107,0,0.6)'
      ctx.shadowBlur = 8 * dpr
      ctx.beginPath()
      const mid = h / 2
      for (let x = 0; x <= w; x += 2 * dpr) {
        const u = x / w
        // calm baseline + a travelling spike (the "thought")
        let y = Math.sin(u * 18 + t * 1.4) * 3 * dpr
        y += Math.sin(u * 7 - t * 0.9) * 2 * dpr
        const spikePos = (t * 0.12) % 1
        const d = Math.abs(u - spikePos)
        if (d < 0.05) y += Math.sin((0.05 - d) / 0.05 * Math.PI) * 26 * dpr
        const d2 = Math.abs(u - ((spikePos + 0.5) % 1))
        if (d2 < 0.03) y += Math.sin((0.03 - d2) / 0.03 * Math.PI) * 14 * dpr
        if (x === 0) ctx.moveTo(x, mid - y)
        else ctx.lineTo(x, mid - y)
      }
      ctx.stroke()
      ctx.shadowBlur = 0
    }

    const loop = () => {
      if (!running) return
      t += 0.016
      draw()
      raf = requestAnimationFrame(loop)
    }

    const io = new IntersectionObserver(
      ([e]) => {
        running = e.isIntersecting
        if (running) { cancelAnimationFrame(raf); loop() }
        else cancelAnimationFrame(raf)
      },
      { rootMargin: '0px' },
    )
    io.observe(host)

    return () => {
      io.disconnect()
      cancelAnimationFrame(raf)
      window.removeEventListener('resize', resize)
    }
  }, [])

  useGSAP(
    () => {
      const ctx = containerRef.current
      if (!ctx) return
      const isMobile = window.innerWidth < 768
      const reduceM = window.matchMedia('(prefers-reduced-motion: reduce)').matches

      /* Scene header */
      gsap.fromTo(
        ctx.querySelector('.signal-header'),
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out', scrollTrigger: { trigger: ctx, start: 'top 80%', toggleActions: 'play none none reverse' } },
      )

      /* Manifesto words — slide in + RGB de-glitch */
      ctx.querySelectorAll<HTMLElement>('.manifesto-word').forEach((word, i) => {
        const span = word.querySelector('span')
        gsap.fromTo(
          word,
          { x: -80, opacity: 0 },
          {
            x: 0, opacity: 1, duration: 0.9, ease: 'power3.out', delay: i * 0.08,
            scrollTrigger: {
              trigger: word, start: 'top 85%', toggleActions: 'play none none reverse',
              onEnter: () => {
                if (span && !(span.textContent || '').includes('THINK')) {
                  span.classList.remove('manifesto-glitch')
                  void span.offsetWidth
                  span.classList.add('manifesto-glitch')
                }
              },
            },
          },
        )
      })

      /* Boot log — the sequence actually RUNS: lines stream in, each OK stamp
         snaps on a beat later, and the pending check RESOLVES to the operator.
         A scan-line sweeps the console frame top→bottom ONCE, time-synced so the
         panel reads as POWERING ON as the OK stamps land. */
      if (reduceM) {
        // Reduced motion: frame lit, scan-line skipped, boot-log fully revealed.
        gsap.set('.boot-line', { opacity: 1, x: 0 })
        gsap.set('.boot-ok', { opacity: 1, scale: 1 })
        gsap.set('.boot-pending-wait', { opacity: 0 })
        gsap.set('.boot-resolved', { opacity: 1, x: 0 })
        gsap.set('.console-scanline', { opacity: 0 })
      } else {
        const bootTl = gsap.timeline({
          scrollTrigger: { trigger: '.console-frame', start: 'top 88%', toggleActions: 'play none none reverse' },
        })
        bootTl
          // scan-line: powers the frame on, synced from t=0 with the streaming lines
          .fromTo('.console-scanline', { top: '0%', opacity: 0 }, { opacity: 1, duration: 0.18, ease: 'power1.out' }, 0)
          .to('.console-scanline', { top: '100%', duration: 1.3, ease: 'none' }, 0)
          .to('.console-scanline', { opacity: 0, duration: 0.3, ease: 'power1.in' }, 1.0)
          .from('.boot-line', { opacity: 0, x: -8, stagger: 0.16, duration: 0.4, ease: 'power1.out' }, 0)
          .fromTo('.boot-ok', { opacity: 0, scale: 1.7 }, { opacity: 1, scale: 1, stagger: 0.16, duration: 0.2, ease: 'power3.out' }, 0.4)
          .to('.boot-pending-wait', { opacity: 0, duration: 0.2 }, '+=0.55')
          .fromTo('.boot-resolved', { opacity: 0, x: -6 }, { opacity: 1, x: 0, duration: 0.35, ease: 'power2.out' }, '<')
      }

      /* Bio — DECODES out of glyph noise: a wavefront sweeps the paragraph and
         each word scrambles hot, then locks to its real text and cools. */
      const bioWords = Array.from(ctx.querySelectorAll<HTMLElement>('.bio-word'))
      if (bioWords.length) {
        const reduceM = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (reduceM) {
          gsap.set(bioWords, { opacity: 1 })
        } else {
          const GLYPHS = '▓▒░#<>/\\|=+*'
          const originals = bioWords.map((el) => el.textContent || '')
          gsap.set(bioWords, { opacity: 0 })
          ScrollTrigger.create({
            trigger: ctx.querySelector('.signal-bio'),
            start: 'top 82%',
            once: true,
            onEnter: () => {
              const n = bioWords.length
              const state = { p: 0 }
              gsap.to(state, {
                p: 1,
                duration: isMobile ? 1.3 : 1.7,
                ease: 'power2.out',
                onUpdate: () => {
                  const front = state.p * (n + 6) // wavefront position (+ tail so the last words finish)
                  bioWords.forEach((el, i) => {
                    if (i > front) return // not reached yet — stays hidden
                    const done = front - i
                    if (done > 4 || state.p >= 1) {
                      if (el.textContent !== originals[i]) el.textContent = originals[i]
                      el.style.opacity = '1'
                      el.style.color = ''
                    } else {
                      const w = originals[i]
                      let s = ''
                      for (let c = 0; c < w.length; c++) {
                        s += Math.random() < done / 4 ? w[c] : GLYPHS[(Math.random() * GLYPHS.length) | 0]
                      }
                      el.textContent = s
                      el.style.opacity = '1'
                      el.style.color = 'rgba(255,145,60,0.8)' // runs hot while decoding
                    }
                  })
                },
                onComplete: () => {
                  bioWords.forEach((el, i) => {
                    el.textContent = originals[i]
                    el.style.opacity = '1'
                    el.style.color = ''
                  })
                },
              })
            },
          })
        }
      }

      /* Metrics — number count-up + HUD reveal */
      ctx.querySelectorAll<HTMLElement>('.metric-item').forEach((el, i) => {
        const numEl = el.querySelector<HTMLElement>('.metric-number')
        const target = METRICS[i].number
        if (reduceM) {
          // Reduced motion: land the counters on their FINAL values, no count-up.
          if (numEl) numEl.textContent = target + METRICS[i].suffix
          gsap.set(el, { opacity: 1, y: 0 })
          return
        }
        const counter = { value: 0 }
        ScrollTrigger.create({
          trigger: el, start: 'top 82%', once: true,
          onEnter: () => {
            gsap.to(counter, { value: target, duration: 2, ease: 'power2.out', onUpdate: () => { if (numEl) numEl.textContent = Math.round(counter.value) + METRICS[i].suffix } })
            gsap.fromTo(el, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.7, ease: 'power3.out', delay: i * 0.08 })
          },
        })
      })
    },
    { scope: containerRef },
  )

  const edge = 'clamp(1.5rem, 4vw, 3.5rem)'

  return (
    <section
      ref={containerRef}
      id="signal"
      data-scene="signal"
      className="relative w-full"
      style={{
        background: 'linear-gradient(to bottom, transparent 0%, var(--color-void) 14vh, var(--color-abyss) 45vh)',
        paddingTop: 'clamp(8rem, 14vh, 12rem)',
      }}
    >
      {/* EEG / oscilloscope brainwave — ambient, behind content */}
      <canvas
        ref={eegRef}
        aria-hidden="true"
        className="absolute pointer-events-none hidden lg:block"
        style={{ left: edge, right: edge, bottom: 'clamp(5rem, 9vh, 8rem)', height: '90px', width: 'auto', opacity: 0.5, zIndex: 1 }}
      />

      {/* Scene header */}
      <div
        className="signal-header flex items-center gap-3 opacity-0"
        style={{ paddingLeft: edge, paddingRight: edge, marginBottom: 'clamp(3rem, 6vh, 5rem)', position: 'relative', zIndex: 3 }}
      >
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>02</span>
        <svg width="10" height="10" viewBox="0 0 100 100" fill="none">
          <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" stroke="var(--color-fire)" strokeWidth="8" fill="none" />
        </svg>
        <span className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-fog)' }}>The Signal</span>
        <a href="/about" className="font-mono uppercase" style={{ fontSize: 'clamp(0.6rem, 0.85vw, 0.75rem)', letterSpacing: '0.2em', color: 'var(--color-stone)', textDecoration: 'none' }} data-cursor-label="Full bio">/ full bio →</a>
      </div>

      {/* ══ Main layout — 3 columns on desktop ══ */}
      <div
        className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] items-start"
        style={{ paddingLeft: edge, paddingRight: edge, gap: 'clamp(1.5rem, 3vw, 3rem)', position: 'relative', zIndex: 3 }}
      >
        {/* ── Column 1: Manifesto ── */}
        <div className="flex flex-col" style={{ containerType: 'inline-size' }}>
          {['I BUILD', 'THINGS', 'THAT', 'THINK.'].map((word, i) => (
            <div
              key={i}
              className={`manifesto-word ${word === 'THINK.' ? '' : 'overflow-hidden'}`}
              style={{ position: word === 'THINK.' ? 'relative' : undefined }}
            >
              {word === 'THINK.' && (
                <div aria-hidden="true" style={{ position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'visible', transform: 'translateY(10%)' }}>
                  <div style={{ position: 'absolute', left: '-10%', top: '-60%', width: '70%', height: '200%', background: 'radial-gradient(ellipse 80% 60% at 40% 50%, rgba(255,107,0,0.35) 0%, rgba(255,107,0,0.12) 40%, transparent 75%)', filter: 'blur(28px)' }} />
                </div>
              )}
              <span
                className="block font-display font-extrabold uppercase leading-[0.9] tracking-[-0.03em]"
                style={{
                  fontSize: 'clamp(2.5rem, 14.5cqi, 7rem)',
                  position: 'relative',
                  zIndex: 1,
                  color: word === 'THINK.' ? 'var(--color-fire)' : 'var(--color-light)',
                  textShadow: word === 'THINK.' ? '0 0 20px rgba(255,107,0,0.9), 0 0 50px rgba(255,107,0,0.5), 0 0 90px rgba(255,107,0,0.25)' : 'none',
                }}
              >
                {word}
              </span>
            </div>
          ))}
        </div>

        {/* ── Column 2: signal.core console — boot log + decoding bio, metrics as footer ── */}
        <div
          className="console-frame relative flex flex-col lg:mt-2"
          style={{
            minHeight: 'clamp(200px, 25vw, 380px)',
            border: '1px solid rgba(255,107,0,0.15)',
            background: 'rgba(255,107,0,0.012)',
            overflow: 'hidden',
          }}
        >
          {/* Four L-shaped corner ticks bracketing the frame */}
          {CONSOLE_CORNERS.map((c, i) => (
            <svg
              key={i}
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              aria-hidden="true"
              style={{ position: 'absolute', zIndex: 4, pointerEvents: 'none', ...c.style }}
            >
              <polyline points={c.points} stroke="var(--color-fire)" strokeWidth="1" fill="none" />
            </svg>
          ))}

          {/* Power-on scan-line — GSAP sweeps it top→bottom once, synced to bootTl */}
          <div
            className="console-scanline"
            aria-hidden="true"
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: '0%',
              height: '2px',
              opacity: 0,
              background:
                'linear-gradient(90deg, transparent, rgba(255,107,0,0.65) 20%, rgba(255,138,0,0.9) 50%, rgba(255,107,0,0.65) 80%, transparent)',
              boxShadow: '0 0 12px rgba(255,107,0,0.55)',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          />

          {/* Title bar */}
          <div
            className="console-titlebar flex items-center gap-2 font-mono"
            style={{
              padding: 'clamp(0.4rem, 1vh, 0.55rem) clamp(0.6rem, 1.5vw, 0.85rem)',
              borderBottom: '1px solid rgba(255,107,0,0.15)',
              fontSize: 'clamp(0.5rem, 0.62vw, 0.58rem)',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              position: 'relative',
              zIndex: 3,
            }}
          >
            <span style={{ color: 'var(--color-fire)' }}>signal.core</span>
            <span style={{ color: 'var(--color-stone)' }}>· tty0</span>
            <span className="inline-flex items-center" style={{ gap: '0.3rem', marginLeft: '0.4rem' }}>
              {[0, 1, 2].map((d) => (
                <span
                  key={d}
                  aria-hidden
                  style={{
                    width: 7,
                    height: 8,
                    clipPath: HEX_DOT,
                    background: d === 0 ? 'var(--color-fire)' : 'rgba(255,107,0,0.28)',
                    boxShadow: d === 0 ? '0 0 6px rgba(255,107,0,0.55)' : 'none',
                  }}
                />
              ))}
            </span>
            <span style={{ marginLeft: 'auto', color: 'var(--color-stone)', letterSpacing: '0.18em' }}>CAIRO 30.0444°N</span>
          </div>

          {/* Body */}
          <div
            className="flex-1"
            style={{ padding: 'clamp(1rem, 2vh, 1.5rem) clamp(0.9rem, 1.8vw, 1.35rem)', position: 'relative', zIndex: 3 }}
          >
            {/* Boot log */}
            <div
              className="boot-log font-mono"
              style={{ fontSize: 'clamp(0.56rem, 0.72vw, 0.7rem)', letterSpacing: '0.06em', marginBottom: 'clamp(1rem, 2vh, 1.5rem)', maxWidth: '24rem' }}
            >
              {BOOT_LINES.map((line, i) => {
                if (line.type === 'head') {
                  return (
                    <div key={i} className="boot-line flex items-center gap-2" style={{ color: 'var(--color-fire)', paddingBottom: '0.5rem' }}>
                      <svg width="10" height="10" viewBox="0 0 100 100" fill="none" aria-hidden="true" style={{ flexShrink: 0 }}>
                        <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" stroke="var(--color-fire)" strokeWidth="9" fill="none" />
                      </svg>
                      <span style={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>{line.text}</span>
                    </div>
                  )
                }
                if (line.type === 'pending') {
                  return (
                    <div key={i} className="boot-line flex items-center gap-2" style={{ color: 'var(--color-fire)', paddingTop: '0.55rem' }}>
                      <span style={{ color: 'var(--color-stone)' }}>▸</span>
                      {/* waiting state → resolves to the operator once the checks land */}
                      <span className="relative inline-flex items-center gap-2" style={{ minHeight: '1em' }}>
                        <span className="boot-pending-wait inline-flex items-center gap-2">
                          <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase' }}>{line.text}</span>
                          <span style={{ color: 'var(--color-stone)' }}>…</span>
                        </span>
                        <span
                          className="boot-resolved absolute left-0 top-0 inline-flex items-center gap-2"
                          style={{ opacity: 0, whiteSpace: 'nowrap' }}
                        >
                          <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-stone)' }}>operator</span>
                          <span style={{ color: 'var(--color-fire)' }}>→</span>
                          <span style={{ letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-fire)', textShadow: '0 0 12px rgba(255,107,0,0.4)' }}>abdelrahman.a</span>
                        </span>
                      </span>
                      <span className="inline-block align-middle" style={{ width: '0.5em', height: '1em', background: 'var(--color-fire)', animation: 'blink 1s step-end infinite' }} />
                    </div>
                  )
                }
                return (
                  <div key={i} className="boot-line flex items-center" style={{ gap: '0.7rem', paddingBlock: '0.45rem', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                    <span aria-hidden style={{ width: 7, height: 8, flexShrink: 0, clipPath: HEX_DOT, background: 'var(--color-fire)', boxShadow: '0 0 6px rgba(255,107,0,0.55)' }} />
                    <span style={{ width: '7ch', flexShrink: 0, color: 'var(--color-stone)', letterSpacing: '0.2em', textTransform: 'uppercase' }}>{line.key}</span>
                    <span style={{ color: 'var(--color-mist)' }}>{line.val}</span>
                    <span className="boot-ok" style={{ marginLeft: 'auto', color: 'var(--color-fire)', letterSpacing: '0.14em' }}>OK</span>
                  </div>
                )
              })}
            </div>

            {/* Decoding bio */}
            <div className="relative" style={{ paddingLeft: 'clamp(1.5rem, 2vw, 2.25rem)' }}>
              {/* Leading hex marker — replaces the banned side-stripe; reinforces the motif */}
              <svg width="11" height="11" viewBox="0 0 100 100" fill="none" aria-hidden="true"
                   style={{ position: 'absolute', left: 0, top: '0.5em' }}>
                <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" stroke="var(--color-fire)" strokeWidth="9" fill="none" />
              </svg>
              <p
                className="signal-bio font-body"
                style={{ fontSize: 'clamp(1.05rem, 1.2vw, 1.15rem)', color: 'var(--color-text-secondary)', lineHeight: 1.8 }}
              >
                {BIO_TEXT.split(' ').map((w, i) => (
                  <span key={i} className="bio-word" style={{ display: 'inline-block', marginRight: '0.28em' }}>{w}</span>
                ))}
              </p>
            </div>
          </div>

          {/* Metrics — console FOOTER status-bar */}
          <div className="console-footer grid grid-cols-3" style={{ columnGap: 'clamp(0.5rem, 1.5vw, 1rem)', borderTop: '1px solid rgba(255,107,0,0.15)', padding: '0 clamp(0.9rem, 1.8vw, 1.35rem) clamp(0.75rem, 1.5vh, 1rem)', position: 'relative', zIndex: 3 }}>
            {METRICS.map((metric, i) => (
              <div
                key={i}
                className="metric-item"
                style={{
                  paddingTop: 'clamp(0.875rem, 1.5vh, 1.25rem)',
                  paddingRight: i < METRICS.length - 1 ? 'clamp(0.75rem, 1.5vw, 1.5rem)' : undefined,
                  paddingLeft: i > 0 ? 'clamp(0.75rem, 1.5vw, 1.5rem)' : undefined,
                  borderRight: i < METRICS.length - 1 ? '1px solid rgba(255,107,0,0.1)' : undefined,
                }}
              >
                <span className="font-mono block" style={{ fontSize: 'clamp(0.5rem, 0.62vw, 0.58rem)', letterSpacing: '0.2em', color: 'var(--color-fire)', marginBottom: '0.5rem', opacity: 0.85 }}>
                  ▸ {metric.label}
                </span>
                <span className="metric-number font-display font-extrabold block" style={{ fontSize: 'clamp(1.2rem, 2.8vw, 2.5rem)', color: 'var(--color-light)', lineHeight: 1, marginBottom: '0.4rem' }}>
                  0{metric.suffix}
                </span>
                <span className="font-mono block" style={{ fontSize: 'clamp(0.5rem, 0.62vw, 0.58rem)', color: 'var(--color-stone)', letterSpacing: '0.08em', lineHeight: 1.4 }}>
                  {metric.sub}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Column 3: Portrait area (HexField face renders here) ── */}
        <div className="portrait-area relative hidden lg:block" style={{ minHeight: 'clamp(300px, 35vw, 500px)' }} />
      </div>

      {/* Mobile portrait spacer */}
      <div className="block lg:hidden" style={{ height: 'clamp(250px, 55vw, 400px)' }} />

      {/* Hex ornament — bottom right */}
      <div className="flex justify-end" style={{ paddingRight: edge, marginTop: 'clamp(2rem, 4vh, 3rem)', position: 'relative', zIndex: 3 }}>
        <svg width="24" height="24" viewBox="0 0 100 100" fill="none" style={{ filter: 'drop-shadow(0 0 6px rgba(255,107,0,0.9)) drop-shadow(0 0 16px rgba(255,107,0,0.5)) drop-shadow(0 0 32px rgba(255,107,0,0.25))', opacity: 0.9 }}>
          <polygon points="50,5 89,27.5 89,72.5 50,95 11,72.5 11,27.5" fill="rgba(255,107,0,0.15)" stroke="var(--color-fire)" strokeWidth="2" />
          <polygon points="50,22 74.2,36 74.2,64 50,78 25.8,64 25.8,36" fill="rgba(255,107,0,0.2)" stroke="rgba(255,107,0,0.6)" strokeWidth="1.2" />
          <polygon points="50,38 60.4,44 60.4,56 50,62 39.6,56 39.6,44" fill="var(--color-fire)" />
        </svg>
      </div>

      {/* Scene divider */}
      <div className="h-px origin-left" style={{ marginTop: 'clamp(4rem, 8vh, 8rem)', marginLeft: edge, marginRight: edge, backgroundColor: 'rgba(255,107,0,0.12)', position: 'relative', zIndex: 3 }} />
    </section>
  )
}
